import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { Checkbox, Button, Popover, cn } from '@c2fo/liquidity';
import { CheckDoubleIcon, TechnicalDifficultyIcon, XmarkIcon } from '@c2fo/liquidity/icons';
import emptystatements from '@/enterprise/assets/emptystatementsgrey.svg?url';
import invoicesSvg from '@/enterprise/assets/invoicesgrey.svg?url';
import NoDataState from '@/enterprise/components/NoDataState';
import { useDivisionDetailsChips } from '@/enterprise/components/OfferBuilder/DivisionDetailsChips';
import PageBottomPortal from '@/enterprise/components/PageBottomPortal/PageBottomPortal';
import Skeleton from '@/enterprise/components/Skeleton';
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
  TableSortDirection,
  TableSortArrow,
} from '@/enterprise/components/Table';
import usePlayableInvoices, {
  PlayableInvoice,
  PlayableInvoiceSearchFilters,
} from '@/enterprise/data/usePlayableInvoices';
import usePlayableInvoiceStats from '@/enterprise/data/usePlayableInvoiceStats';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import InvoiceAdjustmentPopover from '@/enterprise/features/eligibleInvoiceTable/components/InvoiceAdjustmentPopover';
import { InvoiceRulesTooltip } from '@/enterprise/features/eligibleInvoiceTable/EligibleInvoiceTable';
import useGetRecurringRulesForTakerMarkets from '@/enterprise/features/recurringRules/utils/useGetRecurringRulesForTakerMarkets';
import { useReporting } from '@/reporting';
import QueryBoundaries from '@/shared/components/QueryBoundaries';
import useLocaleFormat from '@/utils/useLocaleFormat';
import useRestrictions from '@/utils/useRestrictions';
import useUserHasIndiaDivisions from '@/utils/useUserHasIndiaDivisions';
import EmptyRender from '../../EmptyRender';
import { EditInvoicesReferrer } from './EditInvoices';
import EditInvoicesToolbar from './EditInvoicesToolbar';
import useSelectedInvoices, {
  isPartiallyAdjustedInvoiceCoveringUnmatchedAdjustmentAndIncluded,
} from './useSelectedInvoices';

export type EditInvoicesTableSortKey = 'voucherId' | 'originalDepositDate' | 'daysPaidEarly' | 'amount' | 'minDiscount';
export interface EditInvoicesTableSort {
  key: EditInvoicesTableSortKey;
  direction: TableSortDirection;
}

interface EditInvoicesTablePaginationProps {
  currentCursor: string | null;
  currentPage: number;
  canGoBack: boolean;
  onNextPage: (nextCursor: string) => void;
  onPreviousPage: () => void;
}

interface EditInvoicesTableProps {
  asModal: boolean;
  hasSidebar?: boolean;
  isPending: boolean;
  onSort: (newSort: EditInvoicesTableSort) => void;
  pagination: EditInvoicesTablePaginationProps;
  readOnly?: boolean;
  referrer: EditInvoicesReferrer;
  onToolbarFloatingChange?: (floating: boolean) => void;
  scrollContainer?: HTMLDivElement | null;
  search: string;
  setShowInvoiceCriteriaModal: (open: boolean) => void;
  sort: EditInvoicesTableSort;
  subscribeToMarketStats: boolean;
  tableType: 'included' | 'excluded';
  takerMarket: TakerMarket;
}

const useHeaderColumns = (subscribeToMarketStats: boolean) => {
  const { t } = useTranslation();
  const hasIndiaDivisions = useUserHasIndiaDivisions();

  return [
    {
      accessorKey: 'voucherId',
      header: t('core.invoiceId'),
      enableSorting: true,
      width: 'min-w-96',
    },
    {
      accessorKey: 'originalDepositDate',
      header: hasIndiaDivisions ? t('core.tentativePaymentDate') : t('core.originalDueDate'),
      enableSorting: true,
      width: 'w-52',
    },
    {
      accessorKey: 'daysPaidEarly',
      header: t('core.daysPaidEarly'),
      enableSorting: true,
      width: 'w-52',
    },
    {
      accessorKey: 'amount',
      header: t('core.invoiceAmount'),
      enableSorting: true,
      width: 'w-52',
    },
    ...(subscribeToMarketStats
      ? ([
          {
            accessorKey: 'minDiscount',
            header: t('core.rate'),
            enableSorting: true,
            width: 'w-52',
          },
        ] as const)
      : []),
  ] as const;
};

const EditInvoicesTableComponent = ({
  asModal,
  hasSidebar,
  isPending,
  onSort,
  onToolbarFloatingChange,
  pagination,
  readOnly,
  referrer,
  scrollContainer,
  search,
  setShowInvoiceCriteriaModal,
  subscribeToMarketStats,
  sort,
  tableType,
  takerMarket,
}: EditInvoicesTableProps) => {
  const { t } = useTranslation();
  const { trackEnterpriseEvent } = useReporting();
  const headerColumns = useHeaderColumns(subscribeToMarketStats);
  const { asCurrency, asDateStringText, asNumber, asPercent } = useLocaleFormat();
  const { getRestrictions } = useRestrictions();
  const { canEditInvoices } = getRestrictions([takerMarket]);
  const { getRecurringRulesForTakerMarkets } = useGetRecurringRulesForTakerMarkets();
  const recurringRule = getRecurringRulesForTakerMarkets([takerMarket])[0];
  const hasRecurringRule = !!recurringRule;
  const { hasActiveOffer } = useDivisionDetailsChips(takerMarket);
  const disabledForActiveOffer = asModal && hasActiveOffer;
  const { data } = usePlayableInvoices({
    makerOrganizationUuid: takerMarket.makerOrganizationUuid,
    options: {
      sortBy: sort.key,
      sortDirection: sort.direction === 'asc' ? 'ASC' : 'DESC',
      ...(pagination.currentCursor && { cursor: pagination.currentCursor }),
    },
    search: {
      supplierExcluded: tableType === 'excluded',
      ...(search && { voucherPatterns: [search] }),
    } as PlayableInvoiceSearchFilters,
    takerMarkets: [takerMarket],
  });
  const { data: playableInvoiceStats } = usePlayableInvoiceStats({
    makerOrganizationUuid: takerMarket.makerOrganizationUuid,
    takerMarkets: [takerMarket],
    ...(search && { search: { voucherPatterns: [search] } }),
  });
  const invoices = data?.invoices;
  const nextCursor = data?.pageInfo?.nextCursor;
  const hasNextPage = data?.pageInfo?.hasNextPage;
  const includedAmount = playableInvoiceStats.includedAmount;
  const includedCount = playableInvoiceStats.includedCount;
  const excludedAmount = playableInvoiceStats.excludedAmount;
  const excludedCount = playableInvoiceStats.excludedCount;
  const currentTabCount = tableType === 'excluded' ? excludedCount : includedCount;
  const currentTabAmount = tableType === 'excluded' ? excludedAmount : includedAmount;
  const {
    isHeaderIndeterminate,
    isHeaderChecked,
    handleHeaderCheckboxChange,
    handleInvoiceCheckboxChange,
    isInvoiceSelected,
    state: selectedInvoicesState,
  } = useSelectedInvoices({
    currentCursor: pagination.currentCursor,
    invoices,
    tableType,
  });
  const toolbarRef = useRef<HTMLDivElement>(null);
  const [isToolbarFloating, setIsToolbarFloating] = useState(false);
  const onToolbarFloatingChangeRef = useRef(onToolbarFloatingChange);
  onToolbarFloatingChangeRef.current = onToolbarFloatingChange;

  useEffect(() => {
    const target = toolbarRef.current;

    if (!target) return;

    const root = asModal ? scrollContainer ?? null : null;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const next =
          !entry.isIntersecting &&
          entry.boundingClientRect.height > 0 &&
          entry.boundingClientRect.top < (entry.rootBounds?.top ?? 0);
        setIsToolbarFloating(next);
        onToolbarFloatingChangeRef.current?.(next);
      },
      { root, rootMargin: '0px', threshold: 1 }
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [asModal, scrollContainer]);

  const handleHeaderCheckboxClick = () => {
    const willBeChecked = !isHeaderChecked || isHeaderIndeterminate;

    trackEnterpriseEvent('invoices::select-all-toggled', {
      makerOrgUuid: takerMarket.makerOrganizationUuid,
      marketUuid: takerMarket.marketUuid,
      takerUuid: takerMarket.takerDivisionUuid,
      checked: willBeChecked,
      currentTab: tableType,
      referrer,
    });

    handleHeaderCheckboxChange();
  };

  const handleInvoiceCheckboxClick = (invoice: PlayableInvoice) => {
    const wasSelected = isInvoiceSelected(invoice);

    trackEnterpriseEvent(wasSelected ? 'invoices::invoice-deselected' : 'invoices::invoice-selected', {
      makerOrgUuid: takerMarket.makerOrganizationUuid,
      marketUuid: takerMarket.marketUuid,
      takerUuid: takerMarket.takerDivisionUuid,
      currentTab: tableType,
      referrer,
    });

    handleInvoiceCheckboxChange(invoice);
  };

  const handleNextPage = () => {
    if (nextCursor) {
      trackEnterpriseEvent('invoices::page-changed', {
        makerOrgUuid: takerMarket.makerOrganizationUuid,
        marketUuid: takerMarket.marketUuid,
        takerUuid: takerMarket.takerDivisionUuid,
        direction: 'next',
        currentTab: tableType,
        referrer,
      });

      pagination.onNextPage(nextCursor);
    }
  };

  const handlePreviousPage = () => {
    trackEnterpriseEvent('invoices::page-changed', {
      makerOrgUuid: takerMarket.makerOrganizationUuid,
      marketUuid: takerMarket.marketUuid,
      takerUuid: takerMarket.takerDivisionUuid,
      direction: 'previous',
      currentTab: tableType,
      referrer,
    });

    pagination.onPreviousPage();
  };

  const handleSort = (accessorKey: EditInvoicesTableSortKey) => {
    let direction: TableSortDirection = accessorKey === 'voucherId' ? 'asc' : 'desc';

    if (sort?.key === accessorKey) {
      direction = sort.direction === 'desc' ? 'asc' : 'desc';
    }

    trackEnterpriseEvent('invoices::sorted', {
      makerOrgUuid: takerMarket.makerOrganizationUuid,
      marketUuid: takerMarket.marketUuid,
      takerUuid: takerMarket.takerDivisionUuid,
      sortKey: accessorKey,
      sortDirection: direction,
      currentTab: tableType,
      referrer,
    });

    onSort({ key: accessorKey, direction });
  };

  if (search && invoices?.length === 0) {
    return (
      <NoDataState
        className="py-8"
        icon={<img src={emptystatements} alt="emptystatements" className="h-28 w-28" />}
        title={t('invoices.noMatchingInvoices')}
        message={t('invoices.noMatchingEligibleInvoicesDescription')}
      />
    );
  }

  const baseToolbarProps = {
    currentCursor: pagination.currentCursor,
    currentTabAmount,
    currentTabCount,
    hasActiveOffer: disabledForActiveOffer,
    hasRecurringRule,
    invoices,
    referrer,
    setShowInvoiceCriteriaModal,
    subscribeToMarketStats,
    tableType,
    takerMarket,
  };

  const toolbar = <EditInvoicesToolbar {...baseToolbarProps} isFloating={false} />;
  const floatingToolbar = (
    <div className="px-4 pb-4">
      <div className="pointer-events-auto flex items-center gap-6 overflow-x-auto rounded-xl bg-secondary-500 p-4 text-white shadow-2xl">
        <div className="hidden gap-6 rounded-lg bg-white/10 px-4 py-1 sm:flex">
          <div className="flex justify-between gap-4 text-sm">
            <div className="font-medium">{t('core.included')}</div>
            <div className="text-right">
              <div>
                {t('core.invoiceWithCount', {
                  count: includedCount,
                  formattedCount: asNumber({ value: includedCount }),
                })}
              </div>
              <div className="text-lg font-medium">{asCurrency(includedAmount, takerMarket.currency)}</div>
            </div>
          </div>
          <div className="w-0.5 rounded-full bg-white/80" />
          <div className="flex justify-between gap-4 text-sm">
            <div className="font-medium">{t('core.excluded')}</div>
            <div className="text-right">
              <div>
                {t('core.invoiceWithCount', {
                  count: excludedCount,
                  formattedCount: asNumber({ value: excludedCount }),
                })}
              </div>
              <div className="text-lg font-medium">{asCurrency(excludedAmount, takerMarket.currency)}</div>
            </div>
          </div>
        </div>
        <EditInvoicesToolbar {...baseToolbarProps} isFloating />
      </div>
    </div>
  );

  return (
    <div className="overflow-x-hidden">
      {!readOnly ? (
        <div ref={toolbarRef} className="flex overflow-x-auto py-6" data-intercom-target="invoiceEditToolbar">
          <div className="w-4 shrink-0" />
          {toolbar}
          <div className="w-4 shrink-0" />
        </div>
      ) : null}
      {!asModal && (
        <PageBottomPortal>
          <div
            className={cn('z-10 mx-auto max-w-screen-xl transition-all duration-300 ease-in-out', {
              'translate-y-full': !isToolbarFloating,
              'translate-y-0': isToolbarFloating,
            })}
          >
            <div className="mx-8 lg:mx-10">{floatingToolbar}</div>
          </div>
        </PageBottomPortal>
      )}
      {asModal &&
        createPortal(
          <div
            className={cn(
              'pointer-events-none fixed bottom-0 right-0 z-10 px-6 transition-all duration-300 ease-in-out',
              {
                'left-0 md:left-[272px]': hasSidebar,
                'left-0 md:left-0': !hasSidebar,
                'translate-y-full': !isToolbarFloating,
                'translate-y-0': isToolbarFloating,
              }
            )}
          >
            {floatingToolbar}
          </div>,
          document.body
        )}
      <div className="w-full overflow-y-auto">
        <Table>
          <TableHeader>
            <TableRow>
              {!readOnly && (
                <TableHead className="w-10">
                  {hasRecurringRule && !asModal ? (
                    <InvoiceRulesTooltip>
                      <Checkbox
                        checked={isHeaderChecked}
                        color="secondary"
                        disabled
                        indeterminate={isHeaderIndeterminate}
                      />
                    </InvoiceRulesTooltip>
                  ) : disabledForActiveOffer && asModal ? (
                    <Checkbox
                      checked={isHeaderChecked}
                      color="secondary"
                      disabled
                      indeterminate={isHeaderIndeterminate}
                    />
                  ) : (
                    <Checkbox
                      checked={isHeaderChecked}
                      color="secondary"
                      disabled={!canEditInvoices || (invoices?.length ?? 0) === 0}
                      indeterminate={isHeaderIndeterminate}
                      onChange={handleHeaderCheckboxClick}
                    />
                  )}
                </TableHead>
              )}
              {headerColumns.map((column) =>
                invoices && invoices.length > 0 ? (
                  <TableHead
                    key={column.accessorKey}
                    className={cn(column.width)}
                    isSorted={sort?.key === column.accessorKey}
                    onClick={() => handleSort(column.accessorKey)}
                    aria-sort={
                      sort?.key === column.accessorKey
                        ? sort.direction === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : 'none'
                    }
                  >
                    <TableSortArrow
                      accessorKey={column.accessorKey}
                      sort={sort}
                      textRight={column.accessorKey !== 'voucherId'}
                    >
                      {column.header}
                    </TableSortArrow>
                  </TableHead>
                ) : (
                  <TableHead
                    key={column.accessorKey}
                    className={cn({ 'text-right': column.accessorKey !== 'voucherId' }, column.width)}
                  >
                    {column.header}
                  </TableHead>
                )
              )}
            </TableRow>
          </TableHeader>
          <TableBody loading={isPending}>
            {invoices && invoices.length > 0 ? (
              invoices.map((invoice) => (
                <TableRow key={invoice.voucherId}>
                  {!readOnly && (
                    <TableCell className="w-10">
                      {hasRecurringRule && !asModal ? (
                        <InvoiceRulesTooltip>
                          <Checkbox checked={isInvoiceSelected(invoice)} color="secondary" disabled />
                        </InvoiceRulesTooltip>
                      ) : disabledForActiveOffer && asModal ? (
                        <Checkbox checked={isInvoiceSelected(invoice)} color="secondary" disabled />
                      ) : (
                        <Checkbox
                          checked={isInvoiceSelected(invoice)}
                          color="secondary"
                          disabled={
                            !canEditInvoices ||
                            isPartiallyAdjustedInvoiceCoveringUnmatchedAdjustmentAndIncluded(invoice)
                          }
                          onChange={() => handleInvoiceCheckboxClick(invoice)}
                        />
                      )}
                    </TableCell>
                  )}
                  <TableCell className="max-w-96">
                    {subscribeToMarketStats ? (
                      <div className="flex min-w-0 items-center gap-1">
                        {takerMarket.offerConfig.isEnabled && invoice.isAccepted && (
                          <Popover>
                            <Popover.Trigger>
                              <CheckDoubleIcon className="h-5 w-5 shrink-0 fill-green-700" />
                            </Popover.Trigger>
                            <Popover.Content arrow className="w-48 text-center" side="bottom">
                              {t('invoice.highLikelihoodOfAcceptance')}
                            </Popover.Content>
                          </Popover>
                        )}
                        {takerMarket.offerConfig.isEnabled && !invoice.isAccepted && (
                          <Popover>
                            <Popover.Trigger>
                              <XmarkIcon className="h-5 w-5 shrink-0 fill-text-secondary" />
                            </Popover.Trigger>
                            <Popover.Content arrow className="w-48 text-center" side="bottom">
                              {t('invoice.lowLikelihoodOfAcceptance')}
                            </Popover.Content>
                          </Popover>
                        )}
                        <div className="max-w-96 whitespace-pre-wrap break-words">{invoice.voucherId}</div>
                      </div>
                    ) : (
                      <div className="max-w-96 whitespace-pre-wrap break-words">{invoice.voucherId}</div>
                    )}
                  </TableCell>
                  <TableCell className="w-52 text-right">
                    {invoice.originalDepositDate && asDateStringText({ date: invoice.originalDepositDate })}
                  </TableCell>
                  <TableCell className="w-52 text-right">
                    {t('core.daysWithCount', {
                      count: invoice.daysPaidEarly,
                      formattedCount: asNumber({ value: invoice.daysPaidEarly }),
                    })}
                  </TableCell>
                  <TableCell className="w-52 text-right">
                    {invoice.adjustmentAmount ? (
                      <InvoiceAdjustmentPopover
                        adjustmentAmount={invoice.adjustmentAmount}
                        amount={invoice.amount}
                        coversUnmatched={invoice.coversUnmatched}
                        currency={invoice.currency}
                        originalAmount={invoice.originalAmount}
                      />
                    ) : (
                      asCurrency(invoice.amount, invoice.currency)
                    )}
                  </TableCell>
                  {subscribeToMarketStats ? (
                    <TableCell className="w-52 text-right">
                      <EmptyRender condition={!takerMarket.offerConfig.isEnabled || !!invoice.minDiscount}>
                        {invoice.minDiscount ? (
                          <div>
                            {asPercent({
                              value: invoice.minDiscount / 100,
                              decimals: 2,
                            })}
                          </div>
                        ) : null}
                      </EmptyRender>
                      {invoice.earn && invoice.earn > 0 ? (
                        <div className="text-text-secondary">
                          {`${invoice.earn > 0 ? '-' : ''}${asCurrency(invoice.earn, invoice.currency)}`}
                        </div>
                      ) : null}
                    </TableCell>
                  ) : null}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={headerColumns.length + (readOnly ? 0 : 1) + (subscribeToMarketStats ? 1 : 0)}>
                  <div className="p-4">
                    <NoDataState
                      icon={<img src={invoicesSvg} alt="invoices" className="h-28 w-28" />}
                      message={
                        tableType === 'included'
                          ? t('invoices.noIncludedInvoicesToDisplay')
                          : t('invoices.noExcludedInvoicesToDisplay')
                      }
                    />
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div className="flex items-center justify-between border-t border-stroke bg-canvas px-4 py-3">
        <div className="text-sm text-text-secondary">
          {t('core.pageNumber', { formattedPageNumber: asNumber({ value: pagination.currentPage }) })}
        </div>
        <div>
          <div className="flex items-center justify-end gap-2">
            <Button variant="ancillary" size="sm" onClick={handlePreviousPage} disabled={!pagination.canGoBack}>
              {t('core.previousPage')}
            </Button>
            <Button variant="ancillary" size="sm" onClick={handleNextPage} disabled={!hasNextPage || !nextCursor}>
              {t('core.nextPage')}
            </Button>
          </div>
          {selectedInvoicesState.selectionMode !== 'none' && (
            <div className="mt-1 text-xs text-text-secondary">
              {t('invoices.changingPagesWillClearYourSelectedInvoices')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const EditInvoicesTable = (props: EditInvoicesTableProps) => {
  const { t } = useTranslation();
  const headerColumns = useHeaderColumns(props.subscribeToMarketStats);

  return (
    <QueryBoundaries
      LoadingComponent={() => (
        <div className="overflow-hidden">
          {!props.readOnly && (
            <div className="mx-4 my-6 flex gap-2">
              <Skeleton className="h-8 w-44" />
              <Skeleton className="h-8 w-44" />
              <Skeleton className="h-8 w-44" />
            </div>
          )}
          <Table>
            <TableHeader>
              <TableRow>
                {!props.readOnly && (
                  <TableHead className="w-10">
                    <Checkbox color="secondary" disabled />
                  </TableHead>
                )}
                {headerColumns.map((column) => (
                  <TableHead
                    key={column.accessorKey}
                    className={cn({ 'text-right': column.accessorKey !== 'voucherId' }, column.width)}
                  >
                    {column.header}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableCell
                  colSpan={headerColumns.length + (props.readOnly ? 0 : 1) + (props.subscribeToMarketStats ? 1 : 0)}
                >
                  <div className="p-4">
                    <NoDataState
                      icon={<img src={invoicesSvg} alt="invoices" className="size-28" />}
                      message={t('invoices.fetchingInvoices')}
                    />
                  </div>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
          <div className="flex items-center justify-between border-t border-stroke bg-canvas px-4">
            <div className="h-14" />
          </div>
        </div>
      )}
      ErrorComponent={() => (
        <div className="px-4 pb-6 pt-8">
          <NoDataState
            icon={<TechnicalDifficultyIcon className="size-24 pb-4" />}
            title={t('invoices.errorLoadingInvoices')}
            message={t('invoices.errorLoadingInvoicesDescription')}
            action={
              <Button size="sm" variant="ancillary" onClick={() => window.location.reload()}>
                {t('core.refresh')}
              </Button>
            }
          />
        </div>
      )}
    >
      <EditInvoicesTableComponent {...props} />
    </QueryBoundaries>
  );
};

export default EditInvoicesTable;
